import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Callout, Collapsible, getTestId, Input, isValidObject, StateConfigContext } from '@airtel/air-design-system';
import { debounce, merge } from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Keyboard, View } from 'react-native';
import { ButtonWidget } from '../../../button-widget';
import { LinkListItemWidget } from '../../../link-list-item-widget';
import { getSkycFormWidgetStyles } from '../../SkycFormWidgetStyles';
import { mobileNumberVerificationConfig, successAvatarConfig } from './MobileNumberVerificationConfig';
import { getIsDisabledState, getSubHeading, validateNumberInput } from './MobileNumberVerificationUtils';
export var MobileNumberVerification = props => {
  var _a, _b;
  var _useContext = useContext(StateConfigContext),
    theme = _useContext.theme;
  var _useMemo = useMemo(() => merge({}, mobileNumberVerificationConfig, props), [mobileNumberVerificationConfig, props]),
    _state = _useMemo.state,
    successStateHeading = _useMemo.successStateHeading,
    isExpanded = _useMemo.isExpanded,
    disableToggle = _useMemo.disableToggle,
    autoFormSubmitDebounceDelay = _useMemo.autoFormSubmitDebounceDelay,
    isAutoFormSubmitDebounceAllowed = _useMemo.isAutoFormSubmitDebounceAllowed,
    _calloutProps = _useMemo.calloutProps,
    testID = _useMemo.testID,
    variant = _useMemo.variant,
    _onUserAction = _useMemo.onUserAction,
    id = _useMemo.id,
    onSubmitButtonPressMetaData = _useMemo.onSubmitButtonPressMetaData,
    onValidFormMetaData = _useMemo.onValidFormMetaData,
    onInValidFormMetaData = _useMemo.onInValidFormMetaData,
    onStepDetailsListItemPressMetaData = _useMemo.onStepDetailsListItemPressMetaData,
    onStepDetailsCtaPressMetaData = _useMemo.onStepDetailsCtaPressMetaData,
    _inputData = _useMemo.inputsDataProps,
    _stepDetails = _useMemo.stepDetailsProps,
    submitButtonProps = _useMemo.submitButtonProps,
    style = _useMemo.style;
  var _useState = useState(_inputData === null || _inputData === void 0 ? void 0 : _inputData.mobileNumber),
    mobileNumberInputProps = _useState[0],
    setMobileNumberInputProps = _useState[1];
  var _useState2 = useState(submitButtonProps),
    submitButton = _useState2[0],
    setSubmitButton = _useState2[1];
  var _useState3 = useState(_inputData === null || _inputData === void 0 ? void 0 : _inputData.name),
    nameInputProps = _useState3[0],
    setNameInputProps = _useState3[1];
  var _useState4 = useState(_calloutProps),
    calloutProps = _useState4[0],
    setCalloutProps = _useState4[1];
  var _useState5 = useState(true),
    isButtonDisabled = _useState5[0],
    setIsButtonDisabled = _useState5[1];
  var _useState6 = useState(isExpanded),
    isExpandedState = _useState6[0],
    setIsExpandedState = _useState6[1];
  var _useState7 = useState(_state),
    stateType = _useState7[0],
    setStateType = _useState7[1];
  var _useState8 = useState((_a = _stepDetails === null || _stepDetails === void 0 ? void 0 : _stepDetails.subHeading) !== null && _a !== void 0 ? _a : ''),
    subHeading = _useState8[0],
    setSubHeading = _useState8[1];
  var isDisabledState = stateType === 'disabled';
  var isSuccessState = stateType === 'success';
  var skycFormWidgetStyle = getSkycFormWidgetStyles(props);
  useEffect(() => {
    setSubHeading(_stepDetails === null || _stepDetails === void 0 ? void 0 : _stepDetails.subHeading);
  }, [_stepDetails === null || _stepDetails === void 0 ? void 0 : _stepDetails.subHeading]);
  useEffect(() => {
    var _ref = _inputData || {},
      name = _ref.name;
    setNameInputProps(prev => Object.assign(Object.assign({}, prev), name || {}));
  }, [JSON.stringify(_inputData === null || _inputData === void 0 ? void 0 : _inputData.name)]);
  useEffect(() => {
    setSubmitButton(prev => Object.assign(Object.assign({}, prev), submitButtonProps || {}));
  }, [JSON.stringify(submitButtonProps)]);
  useEffect(() => {
    var _ref2 = _inputData || {},
      mobileNumber = _ref2.mobileNumber;
    setMobileNumberInputProps(prev => {
      var updatedInputProps = Object.assign(Object.assign({}, prev), mobileNumber);
      handleValidation(updatedInputProps);
      return updatedInputProps;
    });
  }, [JSON.stringify(_inputData === null || _inputData === void 0 ? void 0 : _inputData.mobileNumber)]);
  useEffect(() => {
    setCalloutProps(prev => Object.assign(Object.assign({}, prev), _calloutProps || {}));
  }, [JSON.stringify(_calloutProps)]);
  useEffect(() => {
    setStateType(_state);
    setIsExpandedState(_state === 'none');
  }, [_state]);
  useEffect(() => {
    setIsExpandedState(isExpanded);
    if (!isExpanded) Keyboard.dismiss();
  }, [isExpanded]);
  var useDebouncedFunction = (callback, delay) => {
    return useCallback(debounce(callback, delay), [callback, delay]);
  };
  var functionDebounce = useDebouncedFunction((metaData, formData) => {
    _onUserAction === null || _onUserAction === void 0 ? void 0 : _onUserAction(Object.assign({
      id
    }, metaData), Object.assign({
      props
    }, formData));
  }, autoFormSubmitDebounceDelay);
  var handleValidation = formData => {
    var isValid = validateNumberInput(formData);
    setIsButtonDisabled(!isValid);
    var metaData = isValid ? onValidFormMetaData : onInValidFormMetaData;
    var handleUserAction = metaData => {
      if (isValidObject(metaData)) {
        if (isAutoFormSubmitDebounceAllowed) {
          functionDebounce(metaData, formData);
        } else {
          _onUserAction === null || _onUserAction === void 0 ? void 0 : _onUserAction(Object.assign({
            id
          }, metaData), Object.assign({
            props
          }, formData));
        }
      }
    };
    handleUserAction(metaData);
    return isValid;
  };
  var handleTextChange = value => {
    setStateType('none');
    setSubHeading('');
    setCalloutProps({});
    setNameInputProps(prev => Object.assign(Object.assign({}, prev), {
      value: ''
    }));
    var updatedProps = Object.assign(Object.assign({}, mobileNumberInputProps), {
      value,
      hintText: (mobileNumberInputProps === null || mobileNumberInputProps === void 0 ? void 0 : mobileNumberInputProps.state) === 'error' ? '' : mobileNumberInputProps === null || mobileNumberInputProps === void 0 ? void 0 : mobileNumberInputProps.hintText,
      state: 'none',
      showLoader: false
    });
    handleValidation(updatedProps);
    setMobileNumberInputProps(updatedProps);
    (mobileNumberInputProps === null || mobileNumberInputProps === void 0 ? void 0 : mobileNumberInputProps.onChangeMetaData) && (_onUserAction === null || _onUserAction === void 0 ? void 0 : _onUserAction(Object.assign({
      id
    }, mobileNumberInputProps === null || mobileNumberInputProps === void 0 ? void 0 : mobileNumberInputProps.onChangeMetaData), Object.assign({}, updatedProps)));
  };
  var handleOnValidInputMetaData = value => {
    (mobileNumberInputProps === null || mobileNumberInputProps === void 0 ? void 0 : mobileNumberInputProps.onValidInputMetaData) && (_onUserAction === null || _onUserAction === void 0 ? void 0 : _onUserAction(Object.assign({
      id
    }, mobileNumberInputProps === null || mobileNumberInputProps === void 0 ? void 0 : mobileNumberInputProps.onValidInputMetaData), Object.assign(Object.assign({}, mobileNumberInputProps), {
      value
    })));
  };
  var handleOnPressIn = () => {
    (mobileNumberInputProps === null || mobileNumberInputProps === void 0 ? void 0 : mobileNumberInputProps.onClickMetaData) && (_onUserAction === null || _onUserAction === void 0 ? void 0 : _onUserAction(Object.assign({
      id
    }, mobileNumberInputProps === null || mobileNumberInputProps === void 0 ? void 0 : mobileNumberInputProps.onClickMetaData), Object.assign({}, mobileNumberInputProps)));
  };
  var handleChangeButtonPress = () => {
    setIsExpandedState(true);
  };
  return isValidObject(_stepDetails) && _jsx(Collapsible, Object.assign({
    theme: theme,
    childPaddingToken: "SPACING_0",
    titlePaddingToken: "SPACING_0",
    showIcon: false,
    style: [skycFormWidgetStyle.heading, style],
    titleNoVerticalPadding: true,
    titleNoHorizontalPadding: true,
    withCard: true
  }, getTestId(testID, variant + "_Collapsible"), {
    isExpanded: isExpandedState,
    disabled: getIsDisabledState(isDisabledState, disableToggle),
    titleComponent: _jsx(LinkListItemWidget, Object.assign({}, _stepDetails, isSuccessState && !isExpandedState && successStateHeading && {
      heading: successStateHeading
    }, {
      roundedCorners: true,
      theme: theme
    }, getTestId(testID, variant + "_LinkListItemWidget"), {
      noHoverEffect: true,
      noPressEffect: true,
      onPress: onStepDetailsListItemPressMetaData,
      onUserAction: _onUserAction,
      actionTypeProps: !isExpandedState && !isDisabledState && (_stepDetails === null || _stepDetails === void 0 ? void 0 : _stepDetails.actionTypeProps),
      actionTypeOnPressMetaData: onStepDetailsCtaPressMetaData,
      actionTypeOnPress: handleChangeButtonPress
    }, isSuccessState && Object.assign({}, successAvatarConfig), isSuccessState && !isExpandedState && {
      subHeading: getSubHeading(subHeading, mobileNumberInputProps === null || mobileNumberInputProps === void 0 ? void 0 : mobileNumberInputProps.value, nameInputProps === null || nameInputProps === void 0 ? void 0 : nameInputProps.value)
    }, {
      disabled: getIsDisabledState(isDisabledState, _stepDetails === null || _stepDetails === void 0 ? void 0 : _stepDetails.disabled),
      subHeadingNumberOfLines: (_stepDetails === null || _stepDetails === void 0 ? void 0 : _stepDetails.subHeadingNumberOfLines) || 1
    }))
  }, {
    children: _jsxs(View, Object.assign({
      style: skycFormWidgetStyle.content
    }, {
      children: [_jsx(Input, Object.assign({}, mobileNumberInputProps, {
        theme: theme
      }, getTestId(testID, variant + "_MobileNumber_Input"), {
        isDisabled: getIsDisabledState(isDisabledState, mobileNumberInputProps === null || mobileNumberInputProps === void 0 ? void 0 : mobileNumberInputProps.isDisabled),
        onChangeText: handleTextChange,
        containerStyle: skycFormWidgetStyle.inputWidget,
        readOnlyOnPress: handleOnPressIn,
        onValidInput: handleOnValidInputMetaData
      })), isValidObject(calloutProps) && _jsx(Callout, Object.assign({}, calloutProps, {
        theme: theme,
        style: skycFormWidgetStyle.calloutWidget
      }, getTestId(testID, variant + "_Callout"))), isValidObject(nameInputProps) && (nameInputProps === null || nameInputProps === void 0 ? void 0 : nameInputProps.value) && isSuccessState ? _jsx(Input, Object.assign({
        theme: theme
      }, nameInputProps, getTestId(testID, variant + "_name_Input"))) : _jsx(ButtonWidget, Object.assign({}, submitButton, {
        leftCta: Object.assign(Object.assign({}, submitButton === null || submitButton === void 0 ? void 0 : submitButton.leftCta), {
          disabled: ((_b = submitButton === null || submitButton === void 0 ? void 0 : submitButton.leftCta) === null || _b === void 0 ? void 0 : _b.disabled) || getIsDisabledState(isDisabledState, isButtonDisabled),
          theme
        })
      }, getTestId(testID, variant + "_ButtonWidget"), {
        onLeftCtaPress: onSubmitButtonPressMetaData,
        onUserAction: (args, submitButton) => {
          args && (_onUserAction === null || _onUserAction === void 0 ? void 0 : _onUserAction(args, Object.assign(Object.assign({}, submitButton), {
            mobileNumber: mobileNumberInputProps,
            name: nameInputProps
          })));
        },
        style: skycFormWidgetStyle.buttonWidget
      }))]
    }))
  }));
};